import axios from 'axios';
import { Order, CreateOrderDto, UpdateOrderDto } from '@timesheet/schemas';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

export interface OrderImportPreviewRow {
  rowNumber: number;
  valid: boolean;
  error?: string;
  code?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  plafond?: string;
  clientName?: string;
  resolvedClientId?: number;
}

export interface OrderImportPreviewResult {
  total: number;
  created: number;
  valid: number;
  errors: string[];
  rows: OrderImportPreviewRow[];
  unresolvedClients: string[];
}

export interface OrderDeleteImpactUser {
  userId: number;
  userName: string;
  total: number;
  states: string[];
}

export interface OrderDeleteImpact {
  totalTimesheets: number;
  hasApprovedTimesheets: boolean;
  usersWithTimesheets: OrderDeleteImpactUser[];
}

export interface OrderWorkedHoursByUser {
  userId: number;
  userName: string;
  hours: number;
}

export interface OrderWorkedHoursByAssignment {
  assignmentId: number;
  hours: number;
}

export const orderService = {
  getAll: async (): Promise<Order[]> => {
    const response = await axios.get(`${API_URL}/orders`);
    return response.data;
  },

  getById: async (id: number): Promise<Order> => {
    const response = await axios.get(`${API_URL}/orders/${id}`);
    return response.data;
  },

  create: async (data: CreateOrderDto): Promise<Order> => {
    const response = await axios.post(`${API_URL}/orders`, data);
    return response.data;
  },

  update: async (id: number, data: UpdateOrderDto): Promise<Order> => {
    const response = await axios.patch(`${API_URL}/orders/${id}`, data);
    return response.data;
  },

  getDeleteImpact: async (id: number): Promise<OrderDeleteImpact> => {
    const response = await axios.get(`${API_URL}/orders/${id}/delete-impact`);
    return response.data;
  },

  getWorkedHoursByUser: async (id: number): Promise<OrderWorkedHoursByUser[]> => {
    const response = await axios.get(`${API_URL}/orders/${id}/worked-hours-by-user`);
    return response.data;
  },

  getWorkedHoursByAssignment: async (id: number): Promise<OrderWorkedHoursByAssignment[]> => {
    const response = await axios.get(`${API_URL}/orders/${id}/worked-hours-by-assignment`);
    return response.data;
  },

  delete: async (id: number, options?: { force?: boolean }): Promise<void> => {
    await axios.delete(`${API_URL}/orders/${id}`, {
      params: options?.force ? { force: 'true' } : undefined,
    });
  },

  previewImport: async (
    file: File,
    clientMappings: Record<string, number> = {},
  ): Promise<OrderImportPreviewResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('clientMappings', JSON.stringify(clientMappings));

    const response = await axios.post(`${API_URL}/orders/import/preview`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  import: async (
    file: File,
    clientMappings: Record<string, number> = {},
  ): Promise<OrderImportPreviewResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('clientMappings', JSON.stringify(clientMappings));

    const response = await axios.post(`${API_URL}/orders/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  downloadImportTemplate: async (): Promise<Blob> => {
    const response = await axios.get(`${API_URL}/orders/import/template`, {
      responseType: 'arraybuffer',
    });
    return new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  },
};
